import { getImage, Image, resizeImage, Schedule } from '~/utils/metadata';
import { WithRequired } from '~/utils/types';

import { getPlayerManager } from '~/cast/lib';
import { getMediaMetadata } from './utils';

type MediaMetadata = framework.messages.MediaMetadata;
type LiveMediaMetadata = WithRequired<
  MediaMetadata,
  'sectionStartAbsoluteTime' | 'sectionDuration'
>;
type EnsureLiveSchedule = WithRequired<Schedule, 'live'>;

export function updateChannelMetadata(channel: {
  title: string | null;
  images: Image[] | null;
}) {
  const containerMetadata = getContainerMetadata();

  containerMetadata.title = channel.title || undefined;
  const channelImage = channel.images && getImage(channel.images, '4:3');

  const channelImageWidth = 300;
  containerMetadata.containerImages =
    (channelImage && [resizeImage(channelImage, channelImageWidth)]) ||
    undefined;
}

export function updateSchedulesMetadata(schedules: {
  current: EnsureLiveSchedule | undefined;
  next: EnsureLiveSchedule | undefined;
}) {
  const player = getPlayerManager();

  const mediaInformation =
    player.getMediaInformation() ??
    new cast.framework.messages.MediaInformation();

  if (!mediaInformation.customData) {
    mediaInformation.customData = {
      assetId: schedules.current?.live.channelId || '',
      vmsId: schedules.current?.vmsId || '',
      assetType: schedules.current?.type || '',
      isLive: false,
      mainAssetId: '',
      playSource: '',
      senderName: '',
      senderVersion: '',
      actionOrigin: '',
      userOrigin: {
        msoProvider: '',
        playSource: '',
        userLogin: '',
      },
    };
  }

  const { customData } = mediaInformation;
  customData.isLive = true;

  customData.asset = schedules.current;
  player.setMediaInformation(mediaInformation);

  const containerMetadata = getContainerMetadata();
  containerMetadata.sections = [schedules.current, schedules.next]
    .filter((s) => !!s)
    .map(getLiveMediaMetadata);
}

function getContainerMetadata() {
  const queueManager = cast.framework.CastReceiverContext.getInstance()
    .getPlayerManager()
    .getQueueManager()!;
  let containerMetadata = queueManager.getContainerMetadata();
  if (!containerMetadata) {
    containerMetadata = new cast.framework.messages.ContainerMetadata();
    queueManager.setContainerMetadata(containerMetadata);
  }

  return containerMetadata;
}

function getLiveMediaMetadata(schedule: EnsureLiveSchedule) {
  const mediaMetadata = getMediaMetadata(schedule) as MediaMetadata;

  const startTime = schedule.live.startTime.getTime();
  const endTime = schedule.live.endTime.getTime();
  const durationMs = endTime - startTime;

  mediaMetadata.sectionDuration = Math.floor(durationMs / 1000);
  mediaMetadata.sectionStartAbsoluteTime = Math.floor(startTime / 1000);

  return mediaMetadata as LiveMediaMetadata;
}
